import { useRef } from 'react';

import { cx } from '../lib/cx.ts';

type Segment<T extends string> = {
  value: T;
  label: string;
  /** Announced instead of the label where the label is a bare number. */
  srLabel?: string;
};

type SegmentedControlProps<T extends string> = {
  /** Labels the group for screen readers. Required — a bare radiogroup is not usable. */
  label: string;
  segments: readonly Segment<T>[];
  value: T | null;
  onChange: (value: T) => void;
  /**
   * Smaller text and tighter padding, for a group whose labels are words
   * rather than numbers. Four segments reading "3 months", "6 months" and so
   * on do not fit a card on a 320px phone at the default size.
   */
  compact?: boolean;
  className?: string;
};

/**
 * A single-choice control rendered as adjacent buttons. Implemented as a
 * radiogroup with roving focus, so arrow keys move between options exactly as a
 * native radio group does.
 */
export function SegmentedControl<T extends string>({
  label,
  segments,
  value,
  onChange,
  compact = false,
  className,
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);

  function moveFocus(currentIndex: number, delta: number): void {
    const next = (currentIndex + delta + segments.length) % segments.length;
    const segment = segments[next];
    if (segment === undefined) return;

    onChange(segment.value);
    const buttons = containerRef.current?.querySelectorAll('button');
    buttons?.[next]?.focus();
  }

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={label}
      className={cx('flex gap-1 rounded-md border border-line bg-surface-sunken p-1', className)}
    >
      {segments.map((segment, index) => {
        const selected = segment.value === value;

        return (
          <button
            key={segment.value}
            type="button"
            role="radio"
            aria-checked={selected}
            // Roving tabindex: one stop for the whole group.
            tabIndex={selected || (value === null && index === 0) ? 0 : -1}
            onClick={() => onChange(segment.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                moveFocus(index, 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveFocus(index, -1);
              }
            }}
            className={cx(
              /*
               * `min-w-0` matters: a flex item defaults to `min-width: auto`
               * and so refuses to shrink below its longest word, which makes
               * the whole group spill out of the card it sits in rather than
               * compress. Clipping inside the card is bad; overflowing it is
               * worse, and harder to notice.
               */
              'min-h-touch min-w-0 flex-1 rounded-sm font-medium',
              compact ? 'px-2 text-sm' : 'px-3 text-base',
              'transition-colors duration-(--ui-duration-fast)',
              selected
                ? 'bg-accent text-accent-contrast'
                : 'text-muted hover:bg-surface-raised hover:text-text',
            )}
          >
            {segment.srLabel === undefined ? (
              segment.label
            ) : (
              <>
                <span aria-hidden="true">{segment.label}</span>
                <span className="sr-only">{segment.srLabel}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
